import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { TitleCard } from '../components/TitleCard'
import { myReactionsForShow } from '../lib/episodeReaction'
import { noteTap, runTargets } from '../lib/runOffer'
import { SHARED_POSTER_VT_NAME } from '../lib/viewTransition'
import { premierePlan, premiereLine } from '../lib/premierePlan'
import { useParams, useNavigate } from 'react-router-dom'
import { Info, Star, Play, Minus, MoreHorizontal, ListChecks, Flag, Bell, MessageSquare, Heart, Share2, Moon, Zap, Users } from 'lucide-react'
import { InviteDoor } from '../components/InviteDoor'
import { tmdbGet, backdropUrl } from '../lib/tmdb'
import { supabase } from '../lib/supabase'
import { parseShowId, showPath } from '../lib/showUrl'
import { todayKey } from '../lib/datetime'
import { getTracking, getValidSeasons, isEndedStatus, episodeQuality, nextLabel, lastAiredIn } from '../lib/episode'
import { planMark, planRewind } from '../lib/markPlan'
import { paletteFromPoster, setNowPlayingOverride, clearNowPlayingOverride } from '../lib/nowPlaying'
import { primaryNetwork } from '../lib/networks'
import { useAirSchedule } from '../hooks/useAirSchedule'
import { BackButton, Sheet, ActionRow, TONES } from '../components/UI'
import { ConfirmSheet } from '../components/ConfirmSheet'
import { ShowStatusSheet } from '../components/ShowStatusSheet'
import { EpisodeSheet17 } from '../components/EpisodeSheet17'
import { useEpisodeRatings } from '../hooks/useEpisodeRatings'
import { requestPermission, subscribeToPush } from '../lib/push'
import { readFavorites, isFavorite, toggleFavorite } from '../lib/favorites'
import { shareCard } from '../lib/shareCard'
import { usePlans } from '../components/TonightMode'
import { RunSheet17 } from '../components/RunSheet17'
import { availableRemaining } from '../lib/run'
import { WatchProviders } from '../components/WatchProviders'
import { WhereItsPlaying } from '../components/WhereItsPlaying'
import { ShowSynopsis } from '../components/showdetail/ShowSynopsis'
import { AboutThisShow } from '../components/showdetail/AboutThisShow'
import { CharacterVote } from '../components/CharacterVote'
import { ThirdPartyRatings } from '../components/showdetail/ThirdPartyRatings'
import { ReelRating } from '../components/ReelRating'
import { Clapboard, ClapboardClosed } from '../components/ClapboardIcon'
import { ReviewEvolution } from '../components/ReviewEvolution'
import { useAuth } from '../hooks/useAuth'
import { STATUS_LABELS } from '../hooks/useWatchlist'
import { useToast } from '../components/Toast'
import { CompanyLine } from '../components/CompanyLine'
import { CompanionsSheet } from '../components/CompanionsSheet'
import { logEvent } from '../lib/analytics'
import { RevivalBanner } from '../components/RevivalBanner'
import { revivedPosition } from '../lib/revival'
import { startWatchingSession, endWatchSession } from '../lib/watchSessions'
import { WatchingNowIcon } from '../components/WatchingNowIcon'

// ── Show page 17: the living show page ───────────────────────────────────────
// Implemented from the Claude Design mockup "NextEp Show Page" (2026-09-15,
// docs/design-briefs/v17-show-page-living.md). The page has one job: marking
// episodes. Band → progress line → season strip → episode list, and everything
// else lives behind the single "More" sheet.
//   tap a ring         → mark that episode (a ring past "next" cascades from next)
//   drag down the rings→ scrub: rings fill in a wave, chip counts, release commits
//   tap a watched ring → rewind your position to that episode
// The mark path is wl.saveProgress, so undo, wraps and the finale ceremony are
// rebuilt (About & cast, Ratings & reviews).

const EASE_OUT = 'var(--ease-out)'    // cubic-bezier(0.22,1,0.36,1) — the shared token, not a private copy
const SPRING   = 'var(--ease-spring)' // cubic-bezier(0.34,1.2,0.64,1)

const quality = episodeQuality   // lib/episode.js — shared with the Home deck

const fmtAir = (d) => {
  if (!d) return ''
  try { return new Date(d + 'T12:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) } catch { return d }
}

const reduceMotionNow = () => { try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches } catch { return false } }

// The More sheet's tiles wear the tone of what each row does (IconTile vocabulary).
const MORE_TONES = { status: TONES.neutral, season: TONES.amber, fav: TONES.social, run: TONES.amber, share: TONES.neutral, together: TONES.social, aftershow: TONES.social, notify: TONES.live, remove: TONES.danger }

export default function ShowPage17({ wl, user, featureFlags }) {
  const { id: idParam } = useParams()
  const id = parseShowId(idParam)
  const navigate = useNavigate()
  const today = todayKey()
  const { profile } = useAuth()
  const { show: showToast } = useToast()

  // ── Show data: the list's cached blob first, TMDB when it isn't in the list
  const listShow = useMemo(() => (wl?.items || []).find(s => s?.id === id) || null, [wl?.items, id])
  const listReady = wl?.ready !== false
  const [fetched, setFetched] = useState(null)
  useEffect(() => {
    if (!id || listShow || !listReady) return
    let alive = true
    tmdbGet(`/tv/${id}`).then(d => { if (alive) setFetched(d) }).catch(() => {})
    return () => { alive = false }
  }, [id, listShow, listReady])
  const show = listShow || (listReady && fetched?.id === id ? fetched : null)
  // Arrived from the walkthrough's "Open <show>" (Phase 1): one nudge at the Watched button.
  // The flag lives in sessionStorage, not component state: the route mounts twice
  // behind its lazy chunk, and the first mount's state is gone by the second.
  const [fromStart, setFromStart] = useState(false)
  // Hero parallax: the backdrop drifts at a third of the scroll and stretches on iOS
  // overscroll, the depth cue every native detail page has. One rAF per frame,
  // transform only, nothing under reduced motion.
  const heroRef = useRef(null)
  useEffect(() => {
    const el = heroRef.current
    if (!el || reduceMotionNow()) return
    let raf = 0
    const paint = () => {
      raf = 0
      const y = Math.max(window.scrollY || 0, document.scrollingElement?.scrollTop || 0, el.closest('.surface-wide')?.parentElement?.scrollTop || 0)
      if (y < 0) el.style.transform = `translate3d(0, 0, 0) scale(${1 + Math.min(0.25, -y / 600)})`
      else el.style.transform = `translate3d(0, ${Math.min(y, 420) * 0.34}px, 0)`
    }
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(paint) }
    // Capture on document: html is the scroller in the app, but a shell or an emulated
    // viewport can scroll a wrapper instead, and a captured scroll sees both.
    document.addEventListener('scroll', onScroll, { capture: true, passive: true })
    paint()
    return () => { document.removeEventListener('scroll', onScroll, { capture: true }); if (raf) cancelAnimationFrame(raf) }
  }, [show?.id])
  // Your mood trail: the emoji you picked, back on the ring you picked it for.
  const [myMoods, setMyMoods] = useState({})
  useEffect(() => {
    let alive = true
    if (!user?.id || !show?.id) { setMyMoods({}); return }
    myReactionsForShow(user.id, show.id).then(m => { if (alive) setMyMoods(m) })
    // pulse-reacted fires BEFORE the RPC resolves (peek-cancel contract), so wait a beat.
    const onReact = (e) => { if (Number(e.detail?.showId) === Number(show.id)) setTimeout(() => myReactionsForShow(user.id, show.id).then(m => { if (alive) setMyMoods(m) }), 1200) }
    window.addEventListener('nextep:pulse-reacted', onReact)
    return () => { alive = false; window.removeEventListener('nextep:pulse-reacted', onReact) }
  }, [user?.id, show?.id])
  // Run offer: a burst of Next Up "Watched" taps = someone marking a run by hand.
  // 17.148: the offer is a "caught up to…" picker (one chip per remaining aired episode
  // + the whole season), once per SHOW per session. The 17.14x version offered only
  // "the rest of the season", once per session; the week's telemetry had two new members
  // pressing Watched ~90 times each anyway (192 of 354 marks were single next-up taps).
  const burstRef = useRef([])
  const [runOffer, setRunOffer] = useState(null)   // { season } or null
  const noteNextUpTap = (season) => {
    const r = noteTap(burstRef.current); burstRef.current = r.taps
    if (!r.trigger || runOffer) return
    const k = `nextep_run_offer_${show?.id}`
    let seen = false; try { seen = sessionStorage.getItem(k) === '1' } catch {}
    if (seen) return
    try { sessionStorage.setItem(k, '1') } catch {}
    burstRef.current = []
    setRunOffer({ season }); logEvent('run_offer', { show: show?.id, result: 'shown' })
  }
  useEffect(() => {
    if (!show?.id) return
    try { setFromStart(sessionStorage.getItem('nextep_from_start') === String(show.id)) } catch { setFromStart(false) }
  }, [show?.id])
  useEffect(() => {
    if (!fromStart) return
    const done = () => { try { sessionStorage.removeItem('nextep_from_start') } catch {} ; setFromStart(false) }
    window.addEventListener('nextep:progress-marked', done)
    return () => window.removeEventListener('nextep:progress-marked', done)
  }, [fromStart])
  const inList = !!listShow
  const rawProgress = wl?.progress?.[id] || null
  const status   = wl?.statuses?.[id] || null
  const revived = useMemo(() => show ? revivedPosition(show, status, wl?.completedAt?.[id], rawProgress) : null, [show, status, wl?.completedAt, id, rawProgress])
  const progress = revived || rawProgress
  const trackStatus = revived ? 'watching' : status

  useEffect(() => { if (show) setNowPlayingOverride(show); return () => clearNowPlayingOverride() }, [show?.id]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!show?.id) return
    try {
      const key = 'nextep_recently_viewed'
      const prev = JSON.parse(localStorage.getItem(key) || '[]')
      localStorage.setItem(key, JSON.stringify([{ id: show.id, name: show.name, poster: show.poster_path, ts: Date.now() }, ...prev.filter(s => s.id !== show.id)].slice(0, 10)))
    } catch {}
    logEvent('show_view', { show: show.id, src: 'page17' })
  }, [show?.id]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (show?.name && idParam && !/-/.test(idParam)) window.history.replaceState(window.history.state, '', showPath(show.id, show.name))
  }, [show?.name, show?.id, idParam])

  // ── Tint
  const [tint, setTint] = useState(null)
  useEffect(() => {
    if (!show?.poster_path) return
    let alive = true
    paletteFromPoster(show.poster_path).then(p => { if (alive) setTint(p) })
    return () => { alive = false }
  }, [show?.poster_path])
  const tintTop = tint?.raised || '#1e1833'
  const tintMid = tint?.bg || '#120f1f'

  // ── Tracking + status line
  const seasons = useMemo(() => getValidSeasons(show?.seasons), [show?.seasons])
  const air = useAirSchedule(show ? [show] : [])
  const tv = show ? air[show.id] : null
  const statusLine = useMemo(() => {
    if (!show) return ''
    const ended = isEndedStatus(show.status)
    const net = tv?.networkName || primaryNetwork(show)?.name
    if (ended) return `${show.status === 'Canceled' ? 'Cancelled' : 'Ended'} · ${seasons.length} season${seasons.length === 1 ? '' : 's'}`
    const day = tv?.scheduleDays?.[0]
    // A cached next date can lag a day; never print "Next <yesterday>".
    const nxDate = show.next_episode_to_air?.air_date
    const when = day ? `${day}s` : (nxDate && nxDate >= today ? `Next ${fmtAir(nxDate)}` : null)
    return ['Returning', [when, net ? `on ${net}` : null].filter(Boolean).join(' ')].filter(Boolean).join(' · ')
  }, [show, tv, seasons.length])

  // Watching-now state: which episode of THIS show has an open session, so the
  // row icon can actually show it registered (Jason 2026-09-20: "doesn't change
  // state" — it was a static button with no feedback at all). Optimistic set on
  // tap; also loaded on show open so reopening the page while still "watching"
  // shows the lit state, not a reset button.
  const [watchingEp, setWatchingEp] = useState(null)   // { season, episode } | null
  useEffect(() => {
    if (!user?.id || !show?.id) { setWatchingEp(null); return }
    let alive = true
    supabase.from('watch_sessions').select('season, episode')
      .eq('user_id', user.id).eq('show_id', show.id).is('ended_at', null)
      .order('started_at', { ascending: false }).limit(1)
      .then(({ data }) => { if (alive) setWatchingEp(data?.[0] || null) })
    return () => { alive = false }
  }, [user?.id, show?.id])

  // ── Season strip + episodes
  const [selSeason, setSelSeason] = useState(null)
  useEffect(() => { if (show && selSeason == null) setSelSeason(progress?.season || seasons[0]?.season_number || 1) }, [show, progress?.season, seasons, selSeason])
  const [epsBySeason, setEps] = useState({})
  // Tracking lives here, after the episode cache, because the loaded list is a better
  // witness than TMDB's markers on a batch-drop day (lib/episode getTracking:
  // Dead-End Job, 2026-10-30 — one mark read as "All watched" with seven aired rows below).
  const knownAired = useMemo(() => { const ns = progress?.season || 1; return lastAiredIn(epsBySeason[ns], ns, today) }, [epsBySeason, progress?.season, today])
  const tracking = useMemo(() => show ? getTracking(show, progress, trackStatus, today, knownAired) : null, [show, progress, trackStatus, today, knownAired])
  useEffect(() => {
    if (!show?.id || selSeason == null || epsBySeason[selSeason]) return
    let alive = true
    tmdbGet(`/tv/${show.id}/season/${selSeason}`)
      .then(d => { if (alive) setEps(m => ({ ...m, [selSeason]: d?.episodes || [] })) })
      .catch(() => { if (alive) setEps(m => ({ ...m, [selSeason]: [] })) })
    return () => { alive = false }
  }, [show?.id, selSeason]) // eslint-disable-line react-hooks/exhaustive-deps
  const episodes = epsBySeason[selSeason] || null
  // A show on your list with no position yet is at S1·E1, exactly as the Home deck
  // reads it — the two surfaces used to disagree ("Not started" here, S1·E1 there).
  const next = tracking?.next || ((inList && !tracking?.completed && (!trackStatus || trackStatus === 'watching' || trackStatus === 'rewatching')) ? { season: 1, episode: 1 } : null)
  const plan = useMemo(() => (inList && next && trackStatus !== 'completed' && show) ? premierePlan({ seasons: show.seasons, upcoming: show.next_episode_to_air, position: next, today }) : null, [inList, next?.season, next?.episode, trackStatus, show?.id, show?.next_episode_to_air?.air_date, today]) // eslint-disable-line react-hooks/exhaustive-deps
  const planLogged = useRef(null)
  useEffect(() => {
    if (!plan || !show?.id || planLogged.current === show.id) return
    planLogged.current = show.id
    logEvent('premiere_plan_shown', { show: show.id, season: plan.season, days: plan.days, remaining: plan.remaining, src: 'page17' })
  }, [plan, show?.id])
  // The "Next up" row names the next episode even when you're browsing another
  // season, so its season loads too (same cache; one extra fetch at most).
  useEffect(() => {
    const ns = next?.season
    if (!show?.id || ns == null || ns === selSeason || epsBySeason[ns]) return
    let alive = true
    tmdbGet(`/tv/${show.id}/season/${ns}`)
      .then(d => { if (alive) setEps(m => ({ ...m, [ns]: d?.episodes || [] })) })
      .catch(() => { if (alive) setEps(m => ({ ...m, [ns]: [] })) })
    return () => { alive = false }
  }, [show?.id, next?.season, selSeason]) // eslint-disable-line react-hooks/exhaustive-deps
  const isWatched = (ep) => {
    if (trackStatus === 'completed') return true
    if (!next) return false
    return selSeason < next.season || (selSeason === next.season && ep < next.episode)
  }
  const isNext = (ep) => !!next && next.season === selSeason && next.episode === ep
  const aired = (e) => !e.air_date || e.air_date <= today
  // One meta line per episode row and its sheet: "Sep 3 · 47 min", or "Airs Sep 3" for an unaired one.
  const epMeta = (e) => aired(e) ? [fmtAir(e.air_date), e.runtime ? `${e.runtime} min` : null].filter(Boolean).join(' · ') : `Airs ${fmtAir(e.air_date)}`

  // ── Marking
  const commit = useCallback((ep, opts = {}, season = selSeason) => {
    if (!wl?.saveProgress || !show) return
    if (!inList) { wl.add?.(show); return }
    const plan = planMark(show, next, season, ep)
    const firstEver = !progress?.season && season === seasons[0]?.season_number && ep === 1
    wl.saveProgress(show.id, plan.prog, { ...plan.opts, ...opts })
    setWatchingEp(null)   // a real mark already closes the session server-side (endWatchSession); mirror it here
    if (firstEver) showToast(<><span style={{ display: 'block', fontWeight: 600 }}>{show.name}</span><span style={{ display: 'block', color: 'var(--amber)', fontSize: 11, marginTop: 1 }}>You\u2019re underway.</span></>, { icon: '▶', tone: 'amber', duration: 2400, railClaim: 'undo' })
    if (plan.selectSeason !== selSeason) setSelSeason(plan.selectSeason)
    // UndoToast already confirms every mark, everywhere, off the SAME wl.saveProgress
    // call (nextep:progress-marked → the shared "Watched · S·E [Undo]" bar), count and
    // all for a cascade. This used to show its own near-identical toast right on top of
    // it (Jason 2026-09-17: "clapboard, rating... mixed together") — one confirmation,
    // not two competing for the same corner of the screen. The first-ever mark keeps
    // its own toast (different content, and it claims the rail below).
    logEvent('episode_marked', { show: show.id, s: season, e: ep, src: opts.src || (plan.cascade ? 'page17_scrub' : 'page17_ring') })
    try { navigator.vibrate?.(10) } catch {}
  }, [wl, show, inList, selSeason, next, progress?.season, seasons, showToast])

  const rewindTo = async (ep) => {
    if (!wl?.saveProgress || !show) return
    // Un-complete: a finished show stays status=completed after a rewind (saveProgress
    // never downgrades status), so the page kept saying "All watched" and every ring
    // stayed filled. Rewinding IS the un-complete gesture; flip it back to watching.
    if (status === 'completed' || status === 'dropped') await wl?.setStatus?.(show.id, 'watching')
    const r = planRewind(selSeason, ep); wl.saveProgress(show.id, r.prog, r.opts)
  }

  // ── Scrub gesture over the rings: direct DOM per frame, React only at rest
  const listRef = useRef(null)
  const stripRef = useRef(null)
  useEffect(() => {
    const el = stripRef.current, b = el?.querySelector('[data-active]')
    if (!el || !b || typeof el.scrollTo !== 'function') return
    const delta = b.getBoundingClientRect().left - el.getBoundingClientRect().left
    el.scrollTo({ left: Math.max(0, el.scrollLeft + delta - (el.clientWidth - b.offsetWidth) / 2), behavior: 'smooth' })
  }, [selSeason])
  const g = useRef({ active: false, tapOnly: false, from: null, to: null, y0: 0, hold: null, moved: false })
  const [scrubTo, setScrubTo] = useState(null)   // episode number the wave reaches
  const rowAt = (y) => {
    const rows = listRef.current?.querySelectorAll('[data-ep]') || []
    for (const r of rows) { const b = r.getBoundingClientRect(); if (y >= b.top && y <= b.bottom) return Number(r.dataset.ep) }
    return null
  }
  const onRingDown = (e, ep) => {
    const s = g.current
    // Only rings at or past "next" in the current season can start a scrub. Every
    // other aired ring (a watched one, or another season) still takes a TAP — this
    // used to return early without arming the gesture, so pointer-up bailed and a
    // watched ring could never rewind (found by a headless probe, 2026-09-15).
    if (!inList || !next || next.season !== selSeason || ep < next.episode) {
      s.active = true; s.tapOnly = true; s.moved = false; s.from = null; s.to = null; s.y0 = e.clientY; s.x0 = e.clientX
      return
    }
    s.active = true; s.tapOnly = false; s.from = next.episode; s.to = ep; s.y0 = e.clientY; s.moved = false
    try { e.currentTarget.setPointerCapture(e.pointerId); e.currentTarget.setAttribute('data-scrub', '') } catch {}
  }
  const onRingMove = (e) => {
    const s = g.current; if (!s.active) return
    // A tap-only ring (watched, or another season) now owns its touches (touch-action:
    // none, 17.062), so the browser no longer cancels a thumb that lands on it and
    // drags to scroll. Track the drift here and let pointer-up drop it: a 12px move is
    // a scroll attempt, not a rewind.
    if (s.tapOnly) { if (!s.moved && (Math.abs(e.clientY - s.y0) > 12 || Math.abs(e.clientX - s.x0) > 12)) s.moved = true; return }
    if (!s.moved && Math.abs(e.clientY - s.y0) > 8) { s.moved = true; try { navigator.vibrate?.(8) } catch {} }
    if (!s.moved) return
    const ep = rowAt(e.clientY)
    if (ep != null && ep >= s.from && ep !== s.to) {
      const e2 = episodes?.find(x => x.episode_number === ep)
      if (e2 && aired(e2)) { s.to = ep; setScrubTo(ep); try { navigator.vibrate?.(4) } catch {} }
    }
  }
  const onRingUp = (e, ep) => {
    const s = g.current; if (!s.active) return
    s.active = false
    try { e?.currentTarget?.removeAttribute?.('data-scrub') } catch {}
    if (!s.tapOnly && s.moved) { const to = s.to; setScrubTo(null); if (to >= s.from) commit(to) }
    else if (s.tapOnly && s.moved) { /* drifted: a scroll attempt, not a tap */ }
    else onRingTap(ep)
  }
  // An unaired row used to swallow the tap (friction telemetry: "Open E1/E2/E3" dead
  // taps through 2026-10-06 were all future episodes). It can't open the mark sheet,
  // but it can answer: the air date, or that there isn't one yet.
  const openRow = (ep, canAir, e) => {
    if (canAir) { setEpSheet(ep); return }
    showToast(e?.air_date ? `E${ep} airs ${fmtAir(e.air_date)}.` : `E${ep} isn’t scheduled yet.`, { tone: 'amber', duration: 1800 })
  }
  // A ring tap on a show that isn't in your list means two things: add it, AND "I'm
  // through E9". The add used to swallow the mark (and a guest's mint took 0.7s with
  // nothing visible — a dead_tap, 2026-11-02). Remember the tap; the effect below
  // applies it the moment the show is listed and writable. The post-add position
  // sheet is skipped for this add: the tap already answered "where are you with it?".
  const pendingMark = useRef(null)
  const [addingEp, setAddingEp] = useState(null)   // the ring that is waiting on the add (dimmed + busy)
  useEffect(() => {
    const m = pendingMark.current
    if (!m || !inList || !user || !wl?.saveProgress) return
    pendingMark.current = null; setAddingEp(null)
    window.__nextepMarkOnAdd = show?.id
    commit(m.ep, m.opts, m.season)
    setTimeout(() => { if (window.__nextepMarkOnAdd === show?.id) delete window.__nextepMarkOnAdd }, 4000)
  }, [inList, user?.id, wl?.saveProgress])   // eslint-disable-line react-hooks/exhaustive-deps
  const onRingTap = (ep) => {
    if (!inList) {
      pendingMark.current = { ep, opts: { src: 'page17_ring_add' }, season: selSeason }; setAddingEp(ep)
      const forget = () => { pendingMark.current = null; setAddingEp(null) }
      // A signed-in add that rolled back (offline, RLS) must not leave the ring busy or
      // the mark armed for a later, unrelated add. A guest's add() returns false by
      // design (the guest door takes over), so for guests only the safety timer applies.
      Promise.resolve(wl?.add?.(show)).then(ok => { if (ok === false && user) forget() }).catch(forget)
      setTimeout(() => { if (pendingMark.current?.ep === ep) forget() }, 15000)
      return
    }
    // A watched ring rewinds your position to that episode: it and everything after it read unwatched again.
    if (isWatched(ep)) { rewindTo(ep); return }
    commit(ep)
  }
  const scrubCount = scrubTo != null && next ? scrubTo - next.episode + 1 : 0

  const [companyOpen, setCompanyOpen] = useState(false)
  const [inviteOpen, setInviteOpen] = useState(false)   // Watch together (guest invite door, 2026-10-12)
  // ── More sheet
  const [more, setMore] = useState(false)
  const [sub, setSub] = useState(null)   // 'status' | 'about' | 'ratings' | 'where' | 'run'
  const [full, setFull] = useState(null)  // show + credits, fetched the first time About opens
  useEffect(() => {
    if (sub !== 'about' || !show?.id || full?.id === show.id) return
    let alive = true
    tmdbGet(`/tv/${show.id}`, { append_to_response: 'credits,keywords,content_ratings' }).then(d => { if (alive) setFull(d) }).catch(() => {})
    return () => { alive = false }
  }, [sub, show?.id, full?.id])
  const [confirmRemove, setConfirmRemove] = useState(false)
  const [epSheet, setEpSheet] = useState(null)   // episode number open in the episode sheet
  const { ratings: epRatings, rate: rateEp } = useEpisodeRatings(show?.id, user?.id)
  const [notify, setNotify] = useState(null)
  const plans = usePlans()
  const runnable = inList && !!user && availableRemaining(show, progress, today) >= 2
  const [favs, setFavs] = useState(null)
  useEffect(() => { if (user?.id && inList && favs == null) readFavorites().then(setFavs) }, [user?.id, inList, favs])
  const fav = isFavorite(favs, show?.id)
  const onFav = async () => {
    const r = await toggleFavorite(user?.id, show)
    if (r.full) { showToast('Eight favourites is the cap. Take one off first.', { tone: 'amber', duration: 2200 }); return }
    setFavs(r.list); setMore(false)
    showToast(r.added ? `${show.name} is a favourite.` : `${show.name} is off your favourites.`, { icon: <Heart size={15} color="var(--amber)" />, tone: 'amber', duration: 1800 })
  }
  const onShare = async () => {
    setMore(false)
    const r = wl?.ratings?.[show.id]
    const line = status === 'completed' ? `Finished${r ? ` · ${r} reels` : ''}` : next ? `On S${next.season} · E${next.episode}` : status === 'plan_to_watch' ? 'On my list' : ''
    const res = await shareCard({ show, eyebrow: status === 'completed' ? 'Finished' : 'Watching', headline: show.name, line })
    if (res === 'saved') showToast('Card saved to your downloads.', { tone: 'amber', duration: 2000 })
    if (res === 'failed') showToast('Couldn’t build the card just now.', { tone: 'error', duration: 2000 })
  }
  const notifyMe = async () => {
    if (notify) return
    setNotify('pending')
    let perm
    try { perm = await requestPermission(); if (perm === 'granted' && user?.id) await subscribeToPush(user.id) } catch { perm = null }
    setMore(false)
    if (perm !== 'granted') {
      setNotify(null)
      showToast(perm === 'denied' ? 'Notifications are blocked for NextEp in this browser.' : 'Allow notifications to hear when it’s back.', { tone: 'amber', duration: 2400 })
      return
    }
    setNotify('on')
    showToast(`We’ll tell you when ${show.name} is back.`, { icon: <Bell size={15} color="var(--amber)" />, tone: 'amber', duration: 2200 })
  }
  // Mark a whole season: the last aired episode of the selected season, cascading from
  // "next" when it sits in this season, otherwise a straight pointer set (planMark).
  const lastAiredEp = (epsBySeason[selSeason] || []).filter(e => aired(e)).map(e => e.episode_number).pop() || null
  const seasonAllWatched = lastAiredEp != null && isWatched(lastAiredEp)
  const moreRows = [
    ...(inList ? [{ key: 'status', icon: ListChecks, label: 'Status', sub: status ? STATUS_LABELS[status] : null, go: () => setSub('status') }] : []),
    ...(inList && lastAiredEp && !seasonAllWatched && next && next.season === selSeason ? [{ key: 'season', icon: Flag, label: `Mark S${selSeason} watched`, sub: `E1–E${lastAiredEp}`, go: () => { setMore(false); commit(lastAiredEp) } }] : []),
    { key: 'about',    icon: Info,  label: 'About & cast',       go: () => setSub('about') },
    { key: 'ratings',  icon: Star,  label: 'Ratings & reviews',  go: () => setSub('ratings') },
    { key: 'where',    icon: Play,  label: 'Where to watch',     go: () => setSub('where') },
    ...(user && inList && !user.is_anonymous ? [{ key: 'fav', icon: Heart, label: fav ? 'Favourite' : 'Add to favourites', sub: fav ? 'On' : null, go: onFav }] : []),
    ...(runnable ? [{ key: 'run', icon: Zap, label: 'Plan a run', sub: `${availableRemaining(show, progress, today)} waiting`, go: () => setSub('run') }] : []),
    ...(inList ? [{ key: 'share', icon: Share2, label: 'Share a card', sub: null, go: onShare }] : []),
    ...(user && !user.is_anonymous ? [{ key: 'together', icon: Users, label: 'Watch together', sub: 'A friend, or anyone by text. No account needed', go: () => setInviteOpen(true) }] : []),
    ...(user && inList && window.__nextepEnabledFeatures?.has?.('aftershow') ? [{ key: 'aftershow', icon: MessageSquare, label: 'Aftershow', sub: 'Talk about it', go: () => navigate(`/aftershow/${show.id}`) }] : []),
    ...(user && inList && (status === 'completed' || (tracking?.caughtUp && !isEndedStatus(show.status))) ? [{ key: 'notify', icon: Bell, label: notify === 'on' ? 'You’ll hear when it’s back' : 'Tell me when it’s back', sub: notify === 'on' ? 'On' : null, go: notifyMe }] : []),
    ...(inList ? [{ key: 'remove', icon: Minus, label: 'Remove from list', dim: true, go: () => setConfirmRemove(true) }] : []),
  ]

  if (!show) {
    return <TitleCard title="The show" line="Episodes, where to watch, what’s next." caption="Opening the show…" />
  }

  const bd = show.backdrop_path ? backdropUrl(show.backdrop_path, 'w780') : null
  const pct = tracking?.totalCount ? Math.round((tracking.watchedCount / tracking.totalCount) * 100) : 0

  return (
    <div className="surface-wide p17 page-fade" style={{ minHeight: '100dvh', background: 'var(--bg)', fontFamily: 'var(--font)', paddingBottom: 'calc(var(--nav-h) + 24px)', userSelect: 'none', WebkitUserSelect: 'none' }}>
      <style>{`
        @keyframes nxRingBusy { 0%,100% { opacity: 1 } 50% { opacity: 0.45 } }
        @keyframes nxRingPop { 0% { transform: scale(1) } 50% { transform: scale(1.12) } 100% { transform: scale(1) } }
        /* The standout dot breathes: a slow glow that widens and softens, never a
           blink — enough to catch the eye on a scroll past, quiet enough to live
           next to twenty of them on a great season. Each dot starts its cycle at a
           different point (delay off the row) so a column of them shimmers rather
           than flashing in unison. */
        @keyframes nxStandout { 0%, 100% { box-shadow: 0 0 0 2px var(--bg), 0 0 6px rgba(var(--amber-rgb),0.45); transform: scale(1) } 50% { box-shadow: 0 0 0 2px var(--bg), 0 0 14px 2px rgba(var(--amber-rgb),0.85); transform: scale(1.18) } }
        .nx-standout { animation: nxStandout 2.8s ease-in-out infinite; }
        [data-ep]:nth-child(2n) .nx-standout { animation-delay: -0.9s; }
        [data-ep]:nth-child(3n) .nx-standout { animation-delay: -1.8s; }
        @media (prefers-reduced-motion: reduce) { .nx-standout { animation: none; } }
        @keyframes nxChipIn { from { opacity: 0; transform: translateY(6px) } to { opacity: 1; transform: none } }
        .nx-ring { touch-action: pan-y; }
        .nx-ring[data-scrub] { touch-action: none; }
      `}</style>

      {/* Backdrop band, 28% */}
      <div style={{ position: 'relative', height: '28dvh', minHeight: 220, background: `linear-gradient(180deg, ${tintTop} 0%, ${tintMid} 55%, var(--bg) 100%)`, overflow: 'hidden' }}>
        {/* The shared-element target: the tapped poster (lib/viewTransition tags it at click time) grows into this backdrop. */}
        {bd && <img ref={heroRef} src={bd} alt="" draggable="false" style={{ position: 'absolute', inset: '-12% 0 0 0', width: '100%', height: '112%', objectFit: 'cover', display: 'block', opacity: 0.9, willChange: 'transform', transformOrigin: '50% 100%', viewTransitionName: SHARED_POSTER_VT_NAME }} />}
        {/* Scrim stays dark under the title in BOTH themes (light mode put --text-strong
            on a mid-dark photo, 2026-10-12); the fade to the page ground is its own
            strip under the text block. */}
        <div aria-hidden="true" style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(4,4,6,0.15) 0%, rgba(4,4,6,0.58) 55%, rgba(4,4,6,0.78) 100%)' }} />
        <div aria-hidden="true" style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 34, background: 'linear-gradient(180deg, transparent, var(--bg))' }} />
        <div style={{ position: 'absolute', top: 'max(56px, calc(env(safe-area-inset-top) + 14px))', left: 16 }}><BackButton variant="overlay" onClick={() => navigate(-1)} /></div>
        <button aria-label="More" data-tip="More" data-tip-pos="below" data-tip-align="end" onClick={() => setMore(true)} className="ui-hit" style={{ position: 'absolute', top: 'max(56px, calc(env(safe-area-inset-top) + 14px))', right: 16, width: 34, height: 34, borderRadius: 'var(--radius-full)', background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(var(--photo-ink-rgb),0.18)', color: 'rgb(var(--photo-ink-rgb))', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}><MoreHorizontal size={16} /></button>
        <div style={{ position: 'absolute', left: 20, right: 20, bottom: 30, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <h1 style={{ margin: 0, fontFamily: 'var(--font-serif)', fontWeight: 500, fontSize: 28, lineHeight: 1.1, color: 'rgb(var(--photo-ink-rgb))', letterSpacing: '-0.01em', textWrap: 'pretty', textShadow: '0 1px 12px rgba(0,0,0,0.35)' }}>{show.name}</h1>
          <div style={{ fontSize: 13, color: 'rgba(var(--photo-ink-rgb),0.78)' }}>{statusLine}</div>
          <CompanyLine showId={show.id} user={user} me={progress} tone="card" onOpen={() => setCompanyOpen(true)} />
        </div>
      </div>

      {/* Progress line */}
      <div style={{ margin: '20px 20px 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-strong)' }}>
            {!inList ? 'Not in your list' : trackStatus === 'completed' || (tracking?.caughtUp && isEndedStatus(show.status)) ? 'All watched' : next ? nextLabel(tracking, today) : 'Not started'}
          </span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 14, color: 'var(--text-dim)' }}>{tracking ? `${tracking.watchedCount} / ${tracking.totalCount}` : ''}</span>
        </div>
        <div style={{ height: 2, background: 'var(--border)', borderRadius: 999, overflow: 'hidden' }}><div style={{ width: `${pct}%`, height: '100%', background: 'var(--amber)', transition: `width 420ms ${EASE_OUT}` }} /></div>
        {/* "The Return" — fully built (detection + one-tap Resume + dismiss) but
            never actually mounted anywhere in the app (found 2026-09-17 answering
            Jason's "prompt when a finished show gets a new season" ask — the
            mechanism already existed, ShowCard/My List already use revivalInfo()
            for the passive "New season" label, but nothing ever showed the real
            actionable banner). Show-detail is the safe first surface: it only
            renders for a show you've already opened, so this doesn't invent a new
            proactive nudge (Home/notifications) — that's the real ask and is
            being scoped separately, not rushed in here. */}
        {inList && <RevivalBanner show={show} wl={wl} />}
      </div>

      {/* Next up + where to stream — the two things a returning viewer came for,
          above the fold (2026-10-12 declutter). Before this, a show mid-season put
          its next episode under every watched row of that season, and streaming
          lived only behind ⋯ → Where to watch. The row names the episode, says when
          it aired, and marks it in one tap; the strip opens the full provider sheet. */}
      {fromStart && inList && (
        <div className="gm-rise" style={{ margin: '14px 20px 0', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 12, background: 'color-mix(in srgb, var(--amber) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--amber) 40%, transparent)', color: 'var(--text)', fontSize: 13.5, lineHeight: 1.4 }}>
          <span aria-hidden="true" style={{ fontSize: 18 }}>🎬</span>
          <span>Watched S{next?.season || 1} · E{next?.episode || 1}? Tap <b>Watched</b> below and the next one lines up. That’s the whole app.</span>
        </div>
      )}
      {inList && next && !(trackStatus === 'completed' || (tracking?.caughtUp && isEndedStatus(show.status))) && (() => {
        const list = epsBySeason[next.season]           // undefined while loading, [] or [...] once known
        const ne = (list || []).find(e => e.episode_number === next.episode)
        // The episode isn't in the season yet (caught up on a returning show; TMDB posts the
        // next one ~a week ahead). Until 17.149 this card still drew a live Watched button
        // for an episode that doesn't exist, under a headline saying the season was over
        // (Melissa, Contraband S9·E20, 2026-10-27: three title taps and out, twice). Say so,
        // and make the only sensible action the door: tell me when it's back.
        if (Array.isArray(list) && !ne) {
          const canNotify = user && !user.is_anonymous
          const on = notify === 'on'
          return (
            <div style={{ margin: '14px 20px 0', display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 14, background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-dim)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Next up · S{next.season} E{next.episode}</span>
                <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-strong)' }}>Not listed yet</span>
                <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>{tv?.scheduleDays?.[0] ? `${tv.scheduleDays[0]}s, if it continues` : 'It shows here the moment it’s posted'}</span>
              </div>
              {canNotify && (
                <button className="ui-hit" disabled={on || notify === 'pending'} aria-label={on ? 'You’ll hear when it’s back' : 'Tell me when it’s back'} onClick={notifyMe} style={{ flexShrink: 0, height: 40, padding: '0 14px', borderRadius: 'var(--radius-pill)', border: on ? '1px solid var(--border)' : 'none', background: on ? 'transparent' : 'var(--amber)', color: on ? 'var(--text-dim)' : 'var(--amber-ink)', fontFamily: 'var(--font)', fontSize: 14, fontWeight: 700, cursor: on ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: 7 }}>
                  <Bell size={15} />{on ? 'You’ll hear' : 'Tell me'}
                </button>
              )}
            </div>
          )
        }
        const canAir = ne ? aired(ne) : false   // still loading → not tappable yet
        return (
          <div style={{ margin: '14px 20px 0', display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 14, background: 'var(--bg-card)', border: '1px solid rgba(var(--amber-rgb),0.28)' }}>
            <div role="button" tabIndex={0} aria-label={`Open S${next.season} E${next.episode}`} onClick={() => { if (next.season !== selSeason) setSelSeason(next.season); else listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }) }} onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); if (next.season !== selSeason) setSelSeason(next.season) } }} style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2, cursor: 'pointer' }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--amber)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Next up · S{next.season} E{next.episode}</span>
              <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ne?.name || (ne === undefined ? '\u00a0' : `Episode ${next.episode}`)}</span>
              <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>{ne ? epMeta(ne) : '\u00a0'}</span>
            </div>
            <button className="ui-hit" disabled={!canAir} aria-label={canAir ? `Mark S${next.season} E${next.episode} watched` : 'Not aired yet'} onClick={() => { noteNextUpTap(next.season); commit(next.episode, { src: 'page17_nextup' }, next.season) }} style={{ flexShrink: 0, height: 40, padding: '0 16px', borderRadius: 'var(--radius-pill)', border: 'none', background: canAir ? 'var(--amber)' : 'var(--border)', color: canAir ? 'var(--amber-ink)' : 'var(--text-dim)', fontFamily: 'var(--font)', fontSize: 14, fontWeight: 700, cursor: canAir ? 'pointer' : 'default', display: 'flex', alignItems: 'center', gap: 7 }}>
              <Clapboard size={16} watched={false} accent={canAir ? 'var(--amber-ink)' : 'var(--text-dim)'} idle={canAir ? 'var(--amber-ink)' : 'var(--text-dim)'} centered />{canAir ? 'Watched' : ne ? 'Soon' : 'Watched'}
            </button>
          </div>
        )
      })()}
      {/* Ready by the premiere (17.151): behind on a show whose next season has a date?
          Two facts NextEp already holds become a pace instead of a countdown. Pure math
          in lib/premierePlan; nothing to tap, nothing to dismiss. */}
      {plan && (() => {
        return (
          <div role="note" aria-label="Ready by the premiere" style={{ margin: '8px 20px 0', display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderRadius: 12, background: 'rgba(var(--amber-rgb),0.07)', border: '1px solid rgba(var(--amber-rgb),0.22)', fontSize: 13, lineHeight: 1.4, color: 'var(--text-muted)' }}>
            <span aria-hidden="true" style={{ fontSize: 16 }}>🎯</span>
            <span style={{ flex: 1, minWidth: 0 }}><b style={{ color: 'var(--text-strong)' }}>S{plan.season} lands {fmtAir(plan.airDate)}.</b> {premiereLine(plan)}</span>
          </div>
        )
      })()}
      {runOffer && next && (() => {
        const eps = epsBySeason[runOffer.season] || []
        const from = next.season === runOffer.season ? next.episode : 0
        const targets = runTargets(eps, from, today)
        if (!targets.length) return null
        const last = targets[targets.length - 1]
        const sweep = (to, how) => { logEvent('run_offer', { show: show.id, result: 'accepted', how, n: to - from + 1 }); setRunOffer(null); commit(to, { src: 'run_offer' }, runOffer.season) }
        const chip = { flexShrink: 0, height: 34, padding: '0 13px', borderRadius: 'var(--radius-pill)', border: '1px solid rgba(var(--amber-rgb),0.35)', background: 'var(--bg-card)', color: 'var(--text-strong)', fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 600, cursor: 'pointer', letterSpacing: '0.02em' }
        return (
          <div role="group" aria-label="Caught up to" className="page-fade" style={{ margin: '8px 20px 0', padding: '12px 0 12px', borderRadius: 14, background: 'rgba(var(--amber-rgb),0.08)', border: '1px solid rgba(var(--amber-rgb),0.25)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 12px 0 14px' }}>
              <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: 'var(--text-muted)' }}><b style={{ color: 'var(--text-strong)' }}>Caught up further?</b> Tap the last episode you watched.</span>
              <button className="ui-hit" aria-label="Dismiss" onClick={() => { logEvent('run_offer', { show: show.id, result: 'dismissed' }); setRunOffer(null) }} style={{ flexShrink: 0, width: 28, height: 28, borderRadius: 'var(--radius-full)', border: 'none', background: 'transparent', color: 'var(--text-dim)', cursor: 'pointer', fontSize: 16 }}>×</button>
            </div>
            <div className="cascade-in" style={{ display: 'flex', gap: 8, overflowX: 'auto', padding: '10px 14px 2px', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
              {targets.map(ep => (
                <button key={ep} className="ui-hit" aria-label={`Caught up to E${ep}`} onClick={() => sweep(ep, 'episode')} style={chip}>E{ep}</button>
              ))}
              <button className="ui-hit" aria-label={`All of season ${runOffer.season}`} onClick={() => sweep(last, 'season')} style={{ ...chip, background: 'var(--amber)', color: 'var(--amber-ink)', border: 'none', fontFamily: 'var(--font)' }}>All of S{runOffer.season}</button>
            </div>
          </div>
        )
      })()}
      <div role="button" tabIndex={0} aria-label="Where to watch" onClick={() => setSub('where')} onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); setSub('where') } }} style={{ margin: '14px 20px -4px', cursor: 'pointer' }}>
        {/* No position yet → the season chip you're looking at (17.173). */}
        <WatchProviders showId={show.id} season={progress?.season || selSeason} compact />
      </div>

      {/* Season strip */}
      <div ref={stripRef} style={{ margin: '16px 0 0', padding: '0 20px', display: 'flex', gap: 8, alignItems: 'center', overflowX: 'auto', scrollbarWidth: 'none' }}>
        {seasons.map(s => {
          const active = s.season_number === selSeason
          return (
            <button key={s.season_number} data-active={active ? '' : undefined} aria-pressed={active} onClick={() => { if (active) listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); else setSelSeason(s.season_number) }} className="ui-hit" style={{ flexShrink: 0, height: 36, minWidth: 44, padding: '0 14px', borderRadius: 'var(--radius-pill)', border: `1px solid ${active ? 'var(--amber)' : 'var(--border)'}`, background: active ? 'var(--amber)' : 'var(--bg-card)', color: active ? 'var(--amber-ink)' : 'var(--text)', fontFamily: 'var(--font)', fontSize: 14, fontWeight: 600, cursor: 'pointer', transition: 'background 160ms, color 160ms' }}>S{s.season_number}</button>
          )
        })}
      </div>

      {inList && user && seasonAllWatched && (
        <div style={{ margin: '12px 20px 0' }}>
          <button type="button" className="ui-hit" onClick={() => window.dispatchEvent(new CustomEvent('nextep:open-season-wrap', { detail: { showId: show.id, season: selSeason, showName: show.name, poster: show.poster_path, src: 'show_page' } }))}
            style={{ height: 34, padding: '0 14px', borderRadius: 'var(--radius-pill)', border: '1px solid rgba(var(--amber-rgb),0.35)', background: 'rgba(var(--amber-rgb),0.08)', color: 'var(--amber)', fontFamily: 'var(--font)', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            Your S{selSeason} ✦ reels and moods
          </button>
        </div>
      )}

      {/* Episode list */}
      <div ref={listRef} style={{ margin: '16px 20px 0', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {episodes == null && [0, 1, 2, 3, 4, 5].map(i => <div key={i} style={{ height: 50, borderRadius: 10, background: 'var(--bg-card)', opacity: 0.6 }} />)}
        {episodes?.map(e => {
          const ep = e.episode_number
          const watched = isWatched(ep)
          const nxt = isNext(ep)
          const inWave = scrubTo != null && next && ep >= next.episode && ep <= scrubTo
          const waveFrac = inWave ? Math.max(0.3, 1 - (scrubTo - ep) * 0.12) : 0
          const canAir = aired(e)
          const meta = epMeta(e)
          // The quality map (2026-09-20, the v16 episode heatmap folded into the rings
          // instead of a separate grid): an unwatched, aired ring's border brightens
          // with TMDB's community rating, so a season's sag or peak reads at a glance
          // with no extra tap. Amber stays the action colour (next / watched), so the
          // tint runs grey → white, and the standouts (≥ 8.5) get a small amber dot.
          // Under 10 votes the number is noise, so those rings stay plain.
          const q = quality(e)
          const ringBorder = watched ? 'none' : nxt ? '2px solid var(--amber)' : inWave ? `1.5px solid rgba(var(--amber-rgb),${0.3 + waveFrac * 0.7})` : q ? `1.5px solid color-mix(in srgb, var(--text-strong) ${Math.round(q.t * 100)}%, var(--border))` : '1.5px solid var(--border)'
          // No row-level opacity (axe color-contrast, caught 2026-09-17): --text-dim is
          // tuned to ~4.6:1 on its own, but multiplying the WHOLE row by 0.6 (watched) or
          // 0.45 (unaired) crushed both meta spans to 2.69:1. Watched already reads via the
          // filled ring + closed clapboard; unaired already says "Airs {date}" in its own
          // text. Neither needs the row dimmed to be legible as de-emphasized.
          return (
            <div key={ep} data-ep={ep} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 14, minHeight: 50, paddingLeft: nxt ? 12 : 0, transition: 'padding-left 180ms' }}>
              {nxt && <div aria-hidden="true" style={{ position: 'absolute', left: 0, top: 4, bottom: 4, width: 2, borderRadius: 999, background: 'rgba(var(--amber-rgb),0.45)' }} />}
              <button
                className="nx-ring ui-hit"
                aria-label={watched ? `Rewind to E${ep}${myMoods[`${selSeason}-${ep}`] ? `, you felt ${myMoods[`${selSeason}-${ep}`]}` : ''}` : `Mark E${ep} watched`}
                aria-busy={addingEp === ep || undefined}
                disabled={!canAir}
                onPointerDown={(ev) => onRingDown(ev, ep)} onPointerMove={onRingMove} onPointerUp={(ev) => onRingUp(ev, ep)} onPointerCancel={() => { g.current.active = false; setScrubTo(null) }}
                onClick={(ev) => { ev.preventDefault() }}
                onKeyDown={(ev) => { if ((ev.key === 'Enter' || ev.key === ' ') && canAir) { ev.preventDefault(); onRingTap(ep) } }}
                // touch-action: none on the RING ONLY. Without it a thumb that drifts a few
                // px during the tap hands the pointer to the page scroller, pointercancel
                // fires, and neither the tap nor the scrub lands ("Mark E5 watched" dead
                // taps, 2026-10-02). The same class of bug as the deck card in 17.057.
                style={{ width: nxt ? 32 : 28, height: nxt ? 32 : 28, borderRadius: '50%', flexShrink: 0, padding: 0, cursor: canAir ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', touchAction: 'none',
                  border: ringBorder,
                  background: watched ? 'var(--amber)' : inWave ? `rgba(var(--amber-rgb),${waveFrac})` : q && !nxt ? `color-mix(in srgb, var(--text-strong) ${Math.round(q.t * 10)}%, transparent)` : 'transparent',
                  boxShadow: addingEp === ep ? '0 0 0 4px rgba(var(--amber-rgb),0.22)' : inWave && waveFrac > 0.6 ? '0 0 0 4px rgba(var(--amber-rgb),0.18)' : 'none',
                  '--nxa': addingEp === ep ? 'nxRingBusy 0.9s ease-in-out infinite' : inWave ? `nxRingPop 320ms ${SPRING}` : 'none',
                  transition: 'background 180ms ease-out, border-color 180ms ease-out, box-shadow 180ms',
                  position: 'relative' }}>
                {(watched || nxt) && !inWave && <Clapboard size={watched ? 17 : 18} watched={watched} accent={watched ? 'var(--amber-ink)' : 'var(--amber)'} idle="var(--amber)" centered />}
                {watched && !inWave && myMoods[`${selSeason}-${ep}`] && <span aria-hidden="true" style={{ position: 'absolute', right: -6, bottom: -6, fontSize: 12, lineHeight: 1, filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.55))' }}>{myMoods[`${selSeason}-${ep}`]}</span>}
                {q?.standout && !inWave && <span aria-hidden="true" className="nx-standout" style={{ position: 'absolute', top: -2, right: -2, width: 8, height: 8, borderRadius: '50%', background: 'var(--amber)', boxShadow: '0 0 0 2px var(--bg), 0 0 8px rgba(var(--amber-rgb),0.6)' }} />}
              </button>
              {/* Opens for a show that's NOT on your list too (friction telemetry
                  2026-09-21: "Open E1" dead-taps on shows people were only browsing).
                  The sheet's mark button adds the show first via commit(). */}
              <div role="button" tabIndex={0} aria-label={`Open E${ep}`} onClick={() => openRow(ep, canAir, e)} onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') openRow(ep, canAir, e) }} style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1, cursor: canAir ? 'pointer' : 'default' }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-dim)' }}>E{ep}{epRatings?.[`${selSeason}-${ep}`] ? ` · ${epRatings[`${selSeason}-${ep}`]} reels` : ''}{wl?.epNotes?.[show.id]?.[`${selSeason}-${ep}`] ? ' · note' : ''}</span>
                <span style={{ fontSize: 15, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.name || `Episode ${ep}`}</span>
                <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>{meta}</span>
              </div>
              {/* Watching now — row-level, not sheet-only (Jason 2026-09-19: "I'm not
                  seeing it on the episode list", after checking only the row itself, not
                  the tap-through sheet). Only on the ONE "next" row per season, same
                  reasoning as the amber left-border highlight itself — a third icon on
                  every unwatched row would recreate the exact adjacent-tap-target
                  crowding tap-target-quality already flagged; this row is the single
                  exception, same as the moon toggle already is for the row it sits on. */}
              {nxt && user && canAir && !watched && scrubTo !== ep && (() => {
                const on = watchingEp?.season === selSeason && watchingEp?.episode === ep
                return (
                  <button onClick={(ev) => {
                    ev.stopPropagation()
                    if (on) { setWatchingEp(null); endWatchSession(user.id, show.id); return }
                    setWatchingEp({ season: selSeason, episode: ep })   // optimistic — the visible state change this was missing
                    startWatchingSession(wl, user, show, { season: selSeason, episode: ep })
                    showToast('They’ll know you’re watching', { tone: 'default', duration: 2200 })
                  }} aria-label={on ? 'Stop watching — followers were told you were' : 'I’m watching this now'} aria-pressed={on} data-tip={on ? 'Tap to stop' : 'Watching now'} data-tip-pos="below" data-tip-align="end" className="ui-hit" style={{ width: 30, height: 30, borderRadius: 9, border: `1px solid ${on ? 'var(--amber)' : 'var(--border)'}`, background: on ? 'var(--amber)' : 'transparent', color: on ? 'var(--amber-ink)' : 'var(--text-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
                    <WatchingNowIcon size={15} color={on ? 'var(--amber-ink)' : 'var(--text-dim)'} live={on} />
                  </button>
                )
              })()}
              {inList && user && canAir && !watched && scrubTo !== ep && (() => {
                const on = plans?.isPlanned?.(show.id, selSeason, ep)
                return (
                  <button onClick={(ev) => { ev.stopPropagation(); plans?.toggle?.(show, selSeason, ep, e.name || null) }} aria-label={on ? `Take S${selSeason}·E${ep} off Tonight` : `Put S${selSeason}·E${ep} on Tonight`} aria-pressed={!!on} data-tip={on ? 'On Tonight' : 'Put on Tonight'} data-tip-pos="below" data-tip-align="end" className="ui-hit" style={{ width: 30, height: 30, borderRadius: 9, border: `1px solid ${on ? 'var(--amber)' : 'var(--border)'}`, background: on ? 'var(--amber)' : 'transparent', color: on ? 'var(--amber-ink)' : 'var(--text-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
                    <Moon size={14} fill={on ? 'currentColor' : 'none'} />
                  </button>
                )
              })()}
              {scrubTo === ep && (
                <div style={{ height: 28, padding: '0 12px 0 9px', borderRadius: 'var(--radius-pill)', background: 'var(--amber)', color: 'var(--amber-ink)', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', '--nxa': 'nxChipIn 120ms ease-out both' }}>
                  <ClapboardClosed size={15} color="var(--amber-ink)" />{scrubCount} episode{scrubCount === 1 ? '' : 's'}
                </div>
              )}
            </div>
          )
        })}
        {episodes && episodes.length === 0 && <div style={{ color: 'var(--text-dim)', fontSize: 13, padding: '20px 0' }}>No episodes listed for this season yet.</div>}
      </div>

      {!inList && (
        <div style={{ margin: '24px 20px 0' }}>
          <button onClick={() => wl?.add?.(show)} className="ui-primary" style={{ width: '100%', background: 'var(--amber)', color: 'var(--amber-ink)', border: 'none', borderRadius: 'var(--radius-md)', padding: '13px', fontSize: 15, fontWeight: 700, fontFamily: 'var(--font)', cursor: 'pointer' }}>Add to my list</button>
        </div>
      )}

      {/* More sheet — the ONLY door to secondary content */}
      <Sheet open={more && !sub} onClose={() => setMore(false)} label="More" from="bottom" panelStyle={{ padding: '10px 20px calc(34px + env(safe-area-inset-bottom))', background: 'var(--bg-raised)' }}>
        <div style={{ width: 36, height: 4, borderRadius: 999, background: 'var(--border)', margin: '0 auto 16px' }} />
        {moreRows.map((r, i) => (
          <ActionRow key={r.key} index={i} onClick={() => r.go()} icon={r.icon} dim={r.dim} height={52} divider={false}
            tone={MORE_TONES[r.key] || TONES.amber} label={r.label}
            // A short sub ("WATCHING", "E1–E22", "597 WAITING") is a mono badge on the right;
            // a sentence ("A friend, or anyone by text…") reads under the label.
            sub={r.sub && r.sub.length > 16 ? r.sub : null}
            trailing={r.sub && r.sub.length <= 16 ? <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--amber)' }}>{r.sub}</span> : null} />
        ))}
      </Sheet>
      {epSheet != null && (() => {
        const e = (episodes || []).find(x => x.episode_number === epSheet) || { episode_number: epSheet }
        const key = `${selSeason}-${epSheet}`
        return <EpisodeSheet17 show={show} season={selSeason} episode={epSheet} name={e.name} meta={epMeta(e)}
          watched={isWatched(epSheet)} isNext={isNext(epSheet)} aired={aired(e)} inList={inList} community={quality(e)}
          rating={epRatings?.[key] || 0} onRate={(n) => rateEp(selSeason, epSheet, n)}
          note={wl?.epNotes?.[show.id]?.[key] || ''} onSaveNote={inList ? (t) => wl?.saveEpNote?.(show.id, selSeason, epSheet, t) : null}
          onMark={() => commit(epSheet)} onRewind={() => rewindTo(epSheet)} onClose={() => setEpSheet(null)} />
      })()}
      {sub === 'run' && <RunSheet17 show={show} progress={progress} user={user} plans={plans} onClose={() => { setSub(null); setMore(false) }} onToast={(t) => showToast(t, { icon: <Zap size={15} color="var(--amber)" />, tone: 'amber', duration: 1800 })} />}
      {sub === 'status' && <ShowStatusSheet show={show} wl={wl} mode="status" onClose={() => { setSub(null); setMore(false) }} onRemove={() => setConfirmRemove(true)} />}
      <Sheet open={sub === 'about'} onClose={() => { setSub(null); setMore(false) }} label="About & cast" from="bottom" panelStyle={{ maxHeight: '86vh' }}>
        <div style={{ fontFamily: 'var(--font-serif)', color: 'var(--text-strong)', fontSize: 22, marginBottom: 12 }}>{show.name}</div>
        {show.overview && <ShowSynopsis text={show.overview} />}
        <div style={{ marginTop: 16 }}><AboutThisShow show={full?.id === show.id ? { ...show, ...full } : show} /></div>
        {/* A "show-page extra" per the 17 scope (docs/v17-scope.md), same tier as
            Ratings & reviews and Runs — built, backed by a real character_vote_tally
            RPC, but never actually wired into the 17 page until now (found 2026-09-17
            answering "did we ever reimplement the episode fun features?" — no, this one
            hadn't been). Lives here rather than its own More row since it's literally
            about the cast just above it. */}
        {show?.id && <CharacterVote showId={show.id} user={user} />}
      </Sheet>
      <Sheet open={sub === 'ratings'} onClose={() => { setSub(null); setMore(false) }} label="Ratings & reviews" from="bottom" panelStyle={{ maxHeight: '88vh' }}>
        <div style={{ fontFamily: 'var(--font-serif)', color: 'var(--text-strong)', fontSize: 22, marginBottom: 4 }}>Your rating</div>
        <div style={{ color: 'var(--text-dim)', fontSize: 13, marginBottom: 12 }}>{inList ? 'One to five reels for the whole show.' : 'Add the show to rate it.'}</div>
        <ReelRating value={wl?.ratings?.[show.id] || 0} onChange={(n) => inList && wl?.rate?.(show.id, n)} readonly={!inList} />
        <div style={{ height: 1, background: 'var(--border-soft)', margin: '18px 0' }} />
        <ThirdPartyRatings showId={show.id} />
        {user && inList && (
          <>
            <div style={{ height: 1, background: 'var(--border-soft)', margin: '18px 0' }} />
            <ReviewEvolution user={user} show={show} profile={profile} rating={wl?.ratings?.[show.id] || 0} onRate={(v) => wl?.rate?.(show.id, v)} />
          </>
        )}
      </Sheet>
      <WhereItsPlaying show={show} season={progress?.season} user={user} wl={wl} open={sub === 'where'} onClose={() => { setSub(null); setMore(false) }} src="show_page" />
      <ConfirmSheet open={confirmRemove} emoji="🗑️" title={`Remove ${show.name}?`} body="It leaves your list. Your progress and rating are kept if you add it back."
        confirmLabel="Remove" onCancel={() => setConfirmRemove(false)}
        onConfirm={() => { wl.remove(show.id); setConfirmRemove(false); setMore(false); navigate(-1) }} />
      <CompanionsSheet show={show} user={user} progress={progress} open={companyOpen} onClose={() => setCompanyOpen(false)} featureFlags={featureFlags} />
      {inviteOpen && <InviteDoor show={show} user={user} profile={profile} wl={wl} onClose={() => setInviteOpen(false)} />}
    </div>
  )
}
